import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ArrowLeft, 
  Check,
  Sparkles,
  UserCheck,
  Video,
  User,
  Mail,
  Phone,
  ChevronRight,
  Leaf,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

// Simplified health concerns - grouped for easy selection
const HEALTH_CONCERNS = [
  { id: 'digestion', label: 'Digestive Issues', desc: 'Acidity, bloating, constipation, IBS' },
  { id: 'pain', label: 'Pain & Joint Issues', desc: 'Back pain, arthritis, body aches' },
  { id: 'skin', label: 'Skin & Hair', desc: 'Hair loss, acne, eczema, aging' },
  { id: 'stress', label: 'Stress & Sleep', desc: 'Anxiety, insomnia, fatigue, headaches' },
  { id: 'weight', label: 'Weight & Metabolism', desc: 'Weight loss, thyroid, diabetes' },
  { id: 'respiratory', label: 'Respiratory', desc: 'Asthma, allergies, cold, cough' },
  { id: 'heart', label: 'Heart & BP', desc: 'Blood pressure, cholesterol, circulation' },
  { id: 'hormonal', label: 'Hormonal Issues', desc: 'PCOS, menstrual, menopause, fertility' },
];

const PLANS = [
  {
    id: 'basic',
    name: 'AI Assessment',
    price: 1.49,
    originalPrice: 28,
    tag: 'Quick',
    tagColor: 'bg-blue-500',
    icon: Sparkles,
    desc: 'Instant AI-powered assessment',
    disclaimer: 'Not a comprehensive diagnosis or a substitute for professional healthcare advice.'
  },
  {
    id: 'verified',
    name: 'Doctor-Reviewed',
    price: 37,
    originalPrice: 74,
    tag: 'Safe',
    tagColor: 'bg-emerald-500',
    icon: UserCheck,
    desc: 'AI + Doctor validation',
    disclaimer: 'Non-clinical review. Does not replace medical advice.',
    highlighted: true
  },
  {
    id: 'consultation',
    name: '1-on-1 Consultation',
    price: 45,
    originalPrice: 90,
    tag: 'Value',
    tagColor: 'bg-[hsl(22,70%,44%)]',
    icon: Video,
    desc: '45-min video call with doctor',
    disclaimer: 'Ayurvedic diet and lifestyle guidance only.',
    includesAyushCare: true
  }
];

export const NewConsultation = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Single-page state
  const [selectedConcerns, setSelectedConcerns] = useState([]);
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('verified');
  const [clientDetails, setClientDetails] = useState({
    name: '',
    email: '',
    phone: '',
    gender: '',
    age: '',
    disclaimerAccepted: false
  });
  const [loading, setLoading] = useState(false);

  // Pre-fill from Google login
  useEffect(() => {
    if (user) {
      setClientDetails(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
    }
  }, [user]);

  const toggleConcern = (concernId) => {
    setSelectedConcerns(prev => 
      prev.includes(concernId) 
        ? prev.filter(id => id !== concernId)
        : [...prev, concernId]
    );
  };

  const handleSubmit = async () => {
    // Validation
    if (selectedConcerns.length === 0) {
      toast.error('Please select at least one health concern');
      return;
    }
    if (!additionalInfo.trim() || additionalInfo.trim().length < 20) {
      toast.error('Please share details about your health concerns (at least 20 characters)');
      return;
    }
    if (!clientDetails.name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!clientDetails.email.trim() || !/\S+@\S+\.\S+/.test(clientDetails.email)) {
      toast.error('Please enter a valid email');
      return;
    }
    if (!clientDetails.phone.trim() || clientDetails.phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    if (!clientDetails.gender) {
      toast.error('Please select your gender');
      return;
    }
    if (!clientDetails.age || clientDetails.age < 18 || clientDetails.age > 120) {
      toast.error('Please enter a valid age (18+)');
      return;
    }
    if (!clientDetails.disclaimerAccepted) {
      toast.error('Please accept the disclaimer to proceed');
      return;
    }

    setLoading(true);
    try {
      // Convert selected concerns to symptoms format
      const symptoms = selectedConcerns.map(concernId => {
        const concern = HEALTH_CONCERNS.find(c => c.id === concernId);
        return {
          name: concern.label,
          severity: 'moderate',
          duration: 'ongoing',
          description: concern.desc
        };
      });

      // Add detailed description as the main symptom info
      symptoms.push({
        name: 'Detailed Description',
        severity: 'as described',
        duration: 'as described',
        description: additionalInfo.trim()
      });

      const response = await api.post('/api/consultations', { 
        symptoms, 
        plan: selectedPlan,
        client_name: clientDetails.name,
        client_email: clientDetails.email,
        client_phone: clientDetails.phone,
        client_gender: clientDetails.gender,
        client_age: parseInt(clientDetails.age),
        detailed_description: additionalInfo.trim(),
        is_above_18: true,
        disclaimer_accepted: clientDetails.disclaimerAccepted
      });

      const consultationId = response.data.consultation_id;
      toast.success('Consultation created! Proceeding to payment...', { id: 'consultation-created' });
      navigate(`/consultation/${consultationId}/payment`);
    } catch (error) {
      console.error('Error creating consultation:', error);
      toast.error(error.response?.data?.detail || 'Failed to create consultation');
    } finally {
      setLoading(false);
    }
  };

  const selectedPlanData = PLANS.find(p => p.id === selectedPlan);

  return (
    <div className="min-h-screen bg-[hsl(40,23%,96%)]">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/dashboard')} 
            className="flex items-center gap-2 text-stone-600 hover:text-[hsl(22,70%,44%)] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Back</span>
          </button>
          <div className="flex items-center gap-2">
            <Leaf className="w-6 h-6 text-[hsl(22,70%,44%)]" />
            <span className="font-semibold text-[hsl(120,10%,18%)]">New Consultation</span>
          </div>
          <div className="w-16" />
        </div>
      </header>

      {/* Main Content - Single Column Layout */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        
        {/* Step 1: Select Health Concerns */}
        <section className="bg-white rounded-2xl p-6 shadow-sm mb-6">
          <h2 className="text-xl font-bold text-[hsl(120,10%,18%)] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
            What brings you here today?
          </h2>
          <p className="text-stone-500 text-sm mb-5">Select all that apply</p>
          
          <div className="grid grid-cols-2 gap-3">
            {HEALTH_CONCERNS.map(concern => {
              const isSelected = selectedConcerns.includes(concern.id);
              return (
                <button
                  key={concern.id}
                  onClick={() => toggleConcern(concern.id)}
                  className={`p-4 rounded-xl text-left transition-all border-2 ${
                    isSelected 
                      ? 'border-[hsl(22,70%,44%)] bg-[hsl(22,70%,44%)/0.08]' 
                      : 'border-stone-200 hover:border-stone-300 bg-white'
                  }`}
                  data-testid={`concern-${concern.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      isSelected 
                        ? 'border-[hsl(22,70%,44%)] bg-[hsl(22,70%,44%)]' 
                        : 'border-stone-300'
                    }`}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div>
                      <div className="font-semibold text-[hsl(120,10%,18%)] text-sm">{concern.label}</div>
                      <div className="text-xs text-stone-500 mt-0.5">{concern.desc}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Share Details - Required */}
          <div className="mt-5 pt-5 border-t border-stone-100">
            <Label htmlFor="additional" className="text-sm font-medium text-stone-700">
              Share in details <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="additional"
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
              placeholder="Please describe your health concerns in detail - when they started, how they affect your daily life, what you've tried so far, and any other relevant information..."
              className="mt-2 min-h-[100px] resize-none"
              data-testid="additional-info"
              required
            />
            <p className="text-xs text-stone-400 mt-1">This helps us provide a more accurate assessment</p>
          </div>
        </section>

        {/* Step 2: Choose Plan */}
        <section className="bg-white rounded-2xl p-6 shadow-sm mb-6">
          <h2 className="text-xl font-bold text-[hsl(120,10%,18%)] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
            Choose your plan
          </h2>
          <p className="text-stone-500 text-sm mb-5">All plans include a detailed report</p>
          
          <div className="space-y-3">
            {PLANS.map(plan => {
              const Icon = plan.icon;
              const isSelected = selectedPlan === plan.id;
              return (
                <button
                  key={plan.id}
                  onClick={() => setSelectedPlan(plan.id)}
                  className={`w-full p-4 rounded-xl text-left transition-all border-2 ${
                    isSelected 
                      ? 'border-[hsl(22,70%,44%)] bg-[hsl(22,70%,44%)/0.08]' 
                      : 'border-stone-200 hover:border-stone-300 bg-white'
                  }`}
                  data-testid={`plan-${plan.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        isSelected 
                          ? 'border-[hsl(22,70%,44%)] bg-[hsl(22,70%,44%)]' 
                          : 'border-stone-300'
                      }`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        isSelected ? 'bg-[hsl(22,70%,44%)]' : 'bg-stone-100'
                      }`}>
                        <Icon className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-stone-600'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-[hsl(120,10%,18%)]">{plan.name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full text-white ${plan.tagColor}`}>
                            {plan.tag}
                          </span>
                        </div>
                        <div className="text-sm text-stone-500">{plan.desc}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-[hsl(22,70%,44%)]">${plan.price}</div>
                      <div className="text-xs text-stone-400">USD</div>
                    </div>
                  </div>
                  {plan.includesAyushCare && isSelected && (
                    <div className="mt-3 pt-3 border-t border-stone-100">
                      <p className="text-xs text-emerald-600 font-medium">
                        🌿 Includes AYUSH CARE 15 — 15-Day Free Support Plan
                      </p>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        {/* Step 3: Your Details */}
        <section className="bg-white rounded-2xl p-6 shadow-sm mb-6">
          <h2 className="text-xl font-bold text-[hsl(120,10%,18%)] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
            Your details
          </h2>
          <p className="text-stone-500 text-sm mb-5">We&apos;ll send your report to this email</p>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-stone-700 flex items-center gap-2">
                <User className="w-4 h-4" /> Full Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={clientDetails.name}
                onChange={(e) => setClientDetails(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter your full name"
                className="mt-1.5"
                data-testid="client-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="gender" className="text-sm font-medium text-stone-700 flex items-center gap-2">
                  <User className="w-4 h-4" /> Gender <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={clientDetails.gender}
                  onValueChange={(value) => setClientDetails(prev => ({ ...prev, gender: value }))}
                >
                  <SelectTrigger className="mt-1.5" data-testid="client-gender">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                    <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="age" className="text-sm font-medium text-stone-700 flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Age <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="age"
                  type="number"
                  min="18"
                  max="120"
                  value={clientDetails.age}
                  onChange={(e) => setClientDetails(prev => ({ ...prev, age: e.target.value }))}
                  placeholder="Your age"
                  className="mt-1.5"
                  data-testid="client-age"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-stone-700 flex items-center gap-2">
                <Mail className="w-4 h-4" /> Email Address <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={clientDetails.email}
                onChange={(e) => setClientDetails(prev => ({ ...prev, email: e.target.value }))}
                placeholder="your@email.com"
                className="mt-1.5"
                data-testid="client-email"
              />
            </div>
            
            <div>
              <Label htmlFor="phone" className="text-sm font-medium text-stone-700 flex items-center gap-2">
                <Phone className="w-4 h-4" /> Phone Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="phone"
                type="tel"
                value={clientDetails.phone}
                onChange={(e) => setClientDetails(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+1 (555) 000-0000"
                className="mt-1.5"
                data-testid="client-phone"
              />
            </div>

            <div className="flex items-start gap-3 pt-2">
              <Checkbox
                id="disclaimer"
                checked={clientDetails.disclaimerAccepted}
                onCheckedChange={(checked) => setClientDetails(prev => ({ ...prev, disclaimerAccepted: checked }))}
                className="mt-0.5"
                data-testid="disclaimer-checkbox"
              />
              <label htmlFor="disclaimer" className="text-sm text-stone-600 leading-relaxed cursor-pointer">
                I confirm I am over 18 and understand this consultation offers Ayurvedic diet and lifestyle guidance and does not replace medical advice from my healthcare practitioner.
              </label>
            </div>
          </div>
        </section>

        {/* Submit Button */}
        <Button 
          onClick={handleSubmit}
          disabled={loading || selectedConcerns.length === 0}
          className="w-full btn-primary py-6 text-lg font-semibold"
          data-testid="proceed-to-payment"
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              Creating...
            </>
          ) : (
            <>
              Proceed to Payment — ${selectedPlanData?.price}
              <ChevronRight className="w-5 h-5 ml-2" />
            </>
          )}
        </Button>

        <p className="text-center text-xs text-stone-400 mt-4">
          Secure payment powered by Stripe
        </p>
      </main>
    </div>
  );
};
